import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Loader2, CheckCircle2, Circle, AlertTriangle, Languages } from "lucide-react";
import PublicShell, { BrandMark, setSiteLang } from "@/components/PublicShell";
import { api, errMsg, fmtDate } from "@/lib/api";

const L = {
  en: {
    eyebrow: "Employees & volunteers", title: "Check your status", toggle: "Español",
    welcome: "Welcome, and thank you for applying.", welcomeSub: "See where your background check is in just a few seconds.",
    intro: "Enter the code you used to apply (or the reference number from your confirmation email) and your date of birth.",
    code: "Code or reference number", dob: "Date of birth", submit: "Check status", current: "Current step",
    ref: "Reference", submitted: "Submitted", courts: "We are waiting on records from a court. This can take extra time.",
    locked: "This application was not finished within 24 hours, so it was locked. Please contact your organization for a new code.",
    unfinished: "This application was started but not submitted yet.", cont: "Continue your application",
    privacy: "For your privacy, we never show report results here. Questions? Call 302-337-0548.",
    e404: "", e429: "",
    steps: { received: ["Received", "We have your application."], in_progress: ["In progress", "We are checking records."],
      finalizing: ["Final review", "Your report is being finished."], complete: ["Complete", "Results were sent to your organization."] },
  },
  es: {
    eyebrow: "Voluntarios", title: "Consulte su estado", toggle: "English",
    welcome: "Bienvenido, y gracias por ser voluntario.", welcomeSub: "Vea en qué etapa está su verificación de antecedentes en solo unos segundos.",
    intro: "Ingrese el código que usó para presentar su solicitud (o el número de referencia de su correo de confirmación) y su fecha de nacimiento.",
    code: "Código o número de referencia", dob: "Fecha de nacimiento", submit: "Consultar estado", current: "Paso actual",
    ref: "Referencia", submitted: "Enviada", courts: "Estamos esperando registros de un tribunal. Esto puede tomar más tiempo.",
    locked: "Esta solicitud no se terminó dentro de 24 horas, por lo que fue bloqueada. Comuníquese con su organización para obtener un código nuevo.",
    unfinished: "Esta solicitud se comenzó pero todavía no se ha enviado.", cont: "Continúe su solicitud",
    privacy: "Por su privacidad, nunca mostramos los resultados del informe aquí. ¿Preguntas? Llame al 302-337-0548.",
    e404: "No encontramos una solicitud con ese código o número de referencia y esa fecha de nacimiento. Revise ambos e intente de nuevo.",
    e429: "Demasiados intentos. Espere 15 minutos e intente de nuevo.",
    steps: { received: ["Recibida", "Tenemos su solicitud."], in_progress: ["En proceso", "Estamos revisando los registros."],
      finalizing: ["Revisión final", "Su informe se está terminando."], complete: ["Completa", "Los resultados se enviaron a su organización."] },
  },
};
type T = typeof L.en;

function Timeline({ r, t }: { r: any; t: T }) {
  const at = r.stages.indexOf(r.stage);
  return (
    <ol className="mt-6 space-y-4" data-testid="status-timeline">
      {r.stages.map((s: keyof T["steps"], i: number) => {
        const done = i <= at;
        return (
          <li key={s} className="flex items-start gap-3" data-testid={`status-step-${s}`} data-done={done}>
            {done ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#15803D]" /> : <Circle className="mt-0.5 h-5 w-5 shrink-0 text-slate-300" />}
            <div>
              <p className={`font-semibold ${i === at ? "text-[#1B365D]" : done ? "text-[#1A2536]" : "text-[#94A3B8]"}`}>{t.steps[s][0]}{i === at && <span className="ml-2 rounded-full bg-[#FFF1CC] px-2 py-0.5 text-xs text-[#9A5B00]">{t.current}</span>}</p>
              <p className="text-sm text-[#64748B]">{i === 1 && i === at && r.waiting_on_courts ? t.courts : t.steps[s][1]}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Result({ r, t }: { r: any; t: T }) {
  if (r.stage === "not_submitted" || r.stage === "locked")
    return (
      <p className="mt-6 flex items-start gap-2 rounded-xl bg-[#FFF1CC] px-4 py-3 text-sm text-[#7C4A03]" data-testid="status-not-submitted">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        {r.stage === "locked" ? t.locked : <span>{t.unfinished} <Link to="/apply" className="font-semibold underline">{t.cont}</Link>.</span>}
      </p>
    );
  return (
    <>
      <p className="mt-4 text-sm text-[#64748B]" data-testid="status-meta">{t.ref} {r.reference} · {r.organization}{r.submitted_at ? ` · ${t.submitted} ${fmtDate(r.submitted_at)}` : ""}</p>
      <Timeline r={r} t={t} />
    </>
  );
}

export default function StatusLookup() {
  const [params] = useSearchParams();
  const [lang, setLang] = useState<"en" | "es">(() => (params.get("lang") || localStorage.getItem("irs_lang")) === "es" ? "es" : "en");
  const t = L[lang];
  const [code, setCode] = useState(params.get("ref") || "");
  const [dob, setDob] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<any>(null);
  const [r, setR] = useState<any>(null);
  const flip = () => { const n = lang === "es" ? "en" : "es"; setSiteLang(n); setLang(n); };
  useEffect(() => { if (lang === "es") { setSiteLang("es"); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null); setR(null);
    try { setR((await api.post("/public/status-lookup", { code, dob })).data); } catch (x) { setErr(x); }
    setBusy(false);
  };
  const st = err?.response?.status;
  const errText = err && ((st === 404 && t.e404) || (st === 429 && t.e429) || errMsg(err));
  return (
    <PublicShell>
      <div className="mx-auto max-w-xl px-4 py-10 sm:px-6 sm:py-14" lang={lang}>
        <div className="relative mb-10 overflow-hidden rounded-3xl bg-[#0B1F3A] px-6 py-7 shadow-[0_20px_50px_-24px_rgba(11,31,58,0.7)] sm:px-8" data-testid="status-welcome-banner">
          <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#1B365D] opacity-80" />
          <div className="pointer-events-none absolute -right-4 top-6 h-24 w-24 rounded-full border-2 border-[#E8B04A]/30" />
          <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-[#F3C66B] to-[#D99A2B]" />
          <div className="relative">
            <BrandMark light />
            <p className="mt-6 font-heading text-xl font-extrabold tracking-tight text-white sm:text-2xl" data-testid="status-welcome-title">{t.welcome}</p>
            <p className="mt-1.5 text-sm text-white/70">{t.welcomeSub}</p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#E8573A]">{t.eyebrow}</p>
          <button type="button" onClick={flip} className="btn-secondary btn-sm" data-testid="status-language-toggle" translate="no"><Languages className="h-4 w-4" /> {t.toggle}</button>
        </div>
        <h1 className="mt-1 text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="status-title">{t.title}</h1>
        <p className="mt-3 text-[#475569]">{t.intro}</p>
        <form onSubmit={submit} className="card-soft mt-8 space-y-4 p-6 sm:p-8" data-testid="status-lookup-form">
          <label className="block"><span className="field-label">{t.code}</span>
            <input className="field-input font-mono uppercase" required minLength={4} value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. K7M2Q9XA" data-testid="status-code-input" />
          </label>
          <label className="block"><span className="field-label">{t.dob}</span>
            <input type="date" className="field-input" required value={dob} onChange={(e) => setDob(e.target.value)} data-testid="status-dob-input" />
          </label>
          <button className="btn-primary w-full" disabled={busy} data-testid="status-lookup-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} {t.submit}</button>
          {errText && <p className="text-sm font-semibold text-[#991B1B]" data-testid="status-lookup-error">{errText}</p>}
          {r && <div data-testid="status-lookup-result"><Result r={r} t={t} /></div>}
        </form>
        <p className="mt-4 text-xs text-[#94A3B8]">{t.privacy}</p>
      </div>
    </PublicShell>
  );
}
